import { Button } from "@/components/ui/button";
import { useMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/use-auth";
import { useSidebar } from "@/hooks/use-sidebar";
import { useTenant } from "@/hooks/use-tenant";
import { UpgradePlanButton } from "@/components/subscription/upgrade-plan-button";
import { MobileSidebar } from "./sidebar";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  BellIcon,
  SettingsIcon,
  HelpCircleIcon,
  LogOutIcon,
  UserIcon,
  XIcon,
  ExternalLinkIcon,
  FileText
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { useState } from "react";
import { useNotifications } from "@/hooks/use-notifications";

export default function Header() {
  const isMobile = useMobile();
  const { user, logoutMutation } = useAuth();
  const { tenant } = useTenant();
  const { collapsed } = useSidebar();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  
  // Use our database-backed notifications hook
  const { 
    notifications, 
    markAsRead, 
    markAllAsRead, 
    unreadCount,
    isLoading: notificationsLoading
  } = useNotifications();

  // Helper functions
  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const getUserInitials = () => {
    if (!user || !user.fullName) return "U";
    
    const nameParts = user.fullName.split(" ");
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`;
    }
    return nameParts[0][0];
  };
  
  const openExternalHelp = () => {
    window.open('https://help.saasgama.com', '_blank');
    toast({
      title: "Help Center",
      description: "Opening help documentation in a new tab.",
    });
  };
  
  // Handle navigation for clicked notifications
  const onNotificationClick = (notification: any) => {
    setShowNotificationDropdown(false);
    markAsRead(notification.id);
    
    // Navigate based on notification type and action URL
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      return;
    }
    
    // Fallback navigation based on notification type
    switch (notification.type) {
      case 'license_expiry':
      case 'license_created':
      case 'license_assigned':
        if (notification.metadata?.licenseId) {
          navigate(`/licenses/${notification.metadata.licenseId}`);
        } else {
          navigate('/licenses');
        }
        break;
      case 'application':
        if (notification.metadata?.applicationId) {
          navigate(`/applications/${notification.metadata.applicationId}`);
        } else {
          navigate('/applications');
        }
        break;
      case 'subscription_warning':
      case 'payment_success':
        navigate('/subscription');
        break;
      case 'user_guidance':
      case 'welcome':
      case 'profile':
        navigate('/settings');
        break;
      case 'system_alert':
        navigate('/dashboard');
        break;
      default:
        navigate('/dashboard');
        break;
    }
  };

  return (
    <header className={`sticky top-0 z-30 bg-white/80 backdrop-blur-sm border-b border-slate-200/60 shadow-sm animate-in transition-all duration-300 ${collapsed ? 'md:ml-16' : 'md:ml-64'}`}>
      <div className="flex items-center justify-between px-4 md:px-6 py-4">
        {/* Left side - Mobile menu + Welcome */}
        <div className="flex items-center gap-3">
          {isMobile && <MobileSidebar />}
          {!isMobile && (
            <div className="text-slate-600 text-sm font-medium">
              Welcome back, {user?.fullName?.split(' ')[0] || 'User'}
            </div>
          )}
        </div>
        
        {/* Right Nav */}
        <div className="flex items-center gap-3">
          {/* Upgrade Plan button - Hidden on mobile */}
          <div className="hidden sm:block">
            <UpgradePlanButton />
          </div>
          
          {/* Admin Dashboard Link - Only shown for super admins */}
          {user?.isSuperAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="hidden md:flex items-center border-blue-200 text-blue-600 hover:bg-blue-50 button-modern"
              onClick={() => window.location.href = "/admin"}
            >
              <span>Admin Dashboard</span>
              <ExternalLinkIcon className="ml-2 h-3 w-3" />
            </Button>
          )}
          
          {/* Notification Bell */}
          <DropdownMenu open={showNotificationDropdown} onOpenChange={setShowNotificationDropdown}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="relative h-10 w-10 rounded-xl p-0 hover:bg-slate-100 transition-all duration-200 hover:scale-105 button-modern"
              >
                <BellIcon className="h-5 w-5 text-slate-600" />
                {/* Notification indicator - only shown when there are unread notifications */}
                {unreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r from-red-500 to-red-600 text-xs font-bold text-white shadow-lg animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="font-normal flex justify-between items-center">
                <span>Notifications</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 px-2 text-xs" 
                  onClick={() => markAllAsRead()}
                  disabled={unreadCount === 0}
                >
                  Mark all as read
                </Button>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {notifications.length === 0 ? (
                <div className="py-8 px-4 text-center">
                  <div className="text-sm text-muted-foreground">No new notifications</div>
                </div>
              ) : (
                notifications.map(notification => (
                  <DropdownMenuItem 
                    key={notification.id} 
                    className="py-3 px-4 cursor-pointer hover:bg-slate-50"
                    onClick={() => onNotificationClick(notification)}
                  >
                    <div className="flex flex-col space-y-1 w-full">
                      <div className="flex justify-between items-start">
                        <span className="text-sm font-medium leading-none">
                          {notification.title}
                          {!notification.isRead && <span className="ml-2 h-2 w-2 bg-blue-500 rounded-full inline-block"></span>}
                        </span>
                        <span className="text-xs text-muted-foreground">{notification.formattedDate}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{notification.message}</p>
                    </div>
                  </DropdownMenuItem>
                ))
              )}
              
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild className="justify-center">
                <Link href="/notifications" className="text-sm text-center w-full hover:underline flex items-center justify-center" onClick={() => setShowNotificationDropdown(false)}>
                  View all notifications
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Settings - Only show for admin users */}
          {user?.role === 'admin' && (
            <Link href="/settings">
              <Button
                variant="ghost"
                size="sm"
                className="h-10 w-10 rounded-xl p-0 hover:bg-slate-100 transition-all duration-200 hover:scale-105 button-modern"
              >
                <SettingsIcon className="h-5 w-5 text-slate-600" />
              </Button>
            </Link>
          )}
          
          {/* Help */}
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-10 w-10 rounded-xl p-0 hover:bg-slate-100 transition-all duration-200 hover:scale-105 button-modern"
              >
                <HelpCircleIcon className="h-5 w-5 text-slate-600" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Help & Support</DialogTitle>
                <DialogDescription>
                  Get help with using SaaSGama
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col space-y-4 py-4">
                <DialogClose asChild>
                  <Link href="/help">
                    <Button variant="outline" className="w-full justify-start">
                      <HelpCircleIcon className="mr-2 h-4 w-4" />
                      Help Center
                    </Button>
                  </Link>
                </DialogClose>
                <div className="text-sm text-muted-foreground mt-4">
                  <p>SaaSGama Version v1.1.0</p>
                  <p className="mt-2">For urgent assistance, please email support@saasgama.com</p>
                </div>
              </div>
              <DialogClose asChild>
                <Button variant="secondary">Close</Button>
              </DialogClose>
            </DialogContent>
          </Dialog>
          
          {/* User Profile Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative rounded-full h-8 w-8 p-0 ml-1">
                <Avatar>
                  <AvatarFallback className="bg-primary text-white">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {user?.fullName}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/* Only show Profile and Settings for admin users */}
              {user?.role === 'admin' && (
                <>
                  <DropdownMenuItem asChild>
                    <Link href="/settings">
                      <UserIcon className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/settings">
                      <SettingsIcon className="mr-2 h-4 w-4" />
                      <span>Settings</span>
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-500 focus:text-red-500">
                <LogOutIcon className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
